import { Link, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@apollo/client/react";
import { ArrowLeft, Loader2, XCircle, AlertTriangle, Clock } from "lucide-react";

import { GET_STUDENT_INCORRECT_EXERCISES } from "@/graphql/queries/students";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MathContent } from "@/components/math/math-content";

type IncorrectExercise = {
  id: string;
  exerciseKey: string;
  skillKey: string;
  skillName: string | null;
  level: number | null;
  result: string;
  solvedAt: string;
  sessionId: string | null;
  problemHtml: string | null;
  correctAnswer: string | null;
  userAnswer: string | null;
  hint: string | null;
  choices: string[] | null;
};

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString("lt-LT", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function ResultBadge({ result }: { result: string }) {
  if (result === "PARTIAL") {
    return (
      <Badge variant="outline" className="text-amber-700 dark:text-amber-400 border-amber-300">
        <AlertTriangle className="mr-1 h-3 w-3" />
        Iš dalies
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-red-700 dark:text-red-400 border-red-300">
      <XCircle className="mr-1 h-3 w-3" />
      Klaidingai
    </Badge>
  );
}

export default function StudentIncorrectExercisesPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const skillKey = searchParams.get("skill");

  const { data, loading } = useQuery(GET_STUDENT_INCORRECT_EXERCISES, {
    variables: { studentId: id, skillKey },
    skip: !id,
    fetchPolicy: "cache-and-network",
  });

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const student = data?.student;
  const exercises = (data?.studentIncorrectExercises ?? []) as IncorrectExercise[];

  if (!student) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Mokinys nerastas</p>
        <Button asChild className="mt-4">
          <Link to="/students">Grįžti į sąrašą</Link>
        </Button>
      </div>
    );
  }

  const skillNameInTitle =
    skillKey && exercises.length > 0 ? exercises[0].skillName ?? skillKey : null;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to={`/students/${id}/skills`}>
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div className="min-w-0">
          <h1 className="text-3xl font-bold tracking-tight">
            {student.name} — klaidingai atsakytos užduotys
          </h1>
          <p className="text-muted-foreground">
            {skillNameInTitle
              ? `Įgūdis: ${skillNameInTitle}`
              : "Visi įgūdžiai"}
            {" · "}
            {exercises.length}{" "}
            {exercises.length === 1 ? "užduotis" : "užduotys"}
          </p>
        </div>
      </div>

      {exercises.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">
              Nėra klaidingai atsakytų užduočių.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {exercises.map((exercise) => (
            <Card key={exercise.id}>
              <CardContent className="py-4 space-y-3">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <ResultBadge result={exercise.result} />
                      {exercise.skillName && (
                        <Badge variant="secondary" className="text-xs">
                          {exercise.skillName}
                        </Badge>
                      )}
                      {exercise.level && (
                        <Badge variant="outline" className="text-xs">
                          Lygis {exercise.level}
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {formatDate(exercise.solvedAt)}
                    </p>
                  </div>
                  {exercise.sessionId && (
                    <Button asChild variant="outline" size="sm">
                      <Link to={`/sessions/${exercise.sessionId}`}>
                        Atidaryti sesiją
                      </Link>
                    </Button>
                  )}
                </div>

                {exercise.problemHtml && (
                  <MathContent
                    html={exercise.problemHtml}
                    className="exercise-content text-sm border rounded p-3 bg-muted/30 [&_p]:mb-1 [&_svg]:inline-block [&_svg]:max-w-full [&_img]:max-w-xs [&_img]:rounded"
                  />
                )}

                <div className="grid gap-2 text-sm sm:grid-cols-2">
                  <div className="rounded border p-2 bg-red-50/40 dark:bg-red-950/10">
                    <p className="text-xs font-medium text-muted-foreground mb-1">
                      Mokinio atsakymas
                    </p>
                    {exercise.userAnswer ? (
                      <MathContent
                        html={exercise.userAnswer}
                        className="text-red-700 dark:text-red-400 font-medium"
                      />
                    ) : (
                      <span className="text-muted-foreground italic">
                        Nepateiktas
                      </span>
                    )}
                  </div>
                  <div className="rounded border p-2 bg-emerald-50/40 dark:bg-emerald-950/10">
                    <p className="text-xs font-medium text-muted-foreground mb-1">
                      Teisingas atsakymas
                    </p>
                    {exercise.correctAnswer ? (
                      <MathContent
                        html={exercise.correctAnswer}
                        className="text-emerald-700 dark:text-emerald-400 font-medium"
                      />
                    ) : (
                      <span className="text-muted-foreground italic">—</span>
                    )}
                  </div>
                </div>

                {exercise.hint && (
                  <p className="text-xs text-muted-foreground">
                    <strong>Užuomina:</strong>{" "}
                    <MathContent html={exercise.hint} className="inline" />
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
